import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, finalize, forkJoin, from, Observable, of, shareReplay } from 'rxjs';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BazisStorageService } from '@bazis/shared/services/storage.service';
import {
    EntData,
    EntDescription,
    EntList,
    EntPriority,
    EntSchema,
    IncludedEntData,
    relationshipsEntData,
    SchemaType,
    SimpleData,
    SrvEntRelationshipsData,
    SrvRequestFields,
} from '@bazis/shared/models/srv.types';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { BazisTransitModalComponent } from '@bazis/form/components/helpers/transit-modal.component';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { buildFilterStr } from '@bazis/utils';
import { BazisToastService } from '@bazis/shared/services/toast.service';

/**
 * **BazisEntityService**
 *
 * Main client-side entity API service.
 * Provides methods to load entities and lists from backend, cache them in `BazisStorageService`,
 * load and cache schemas, and perform common entity operations (create/update/delete/transit/copy).
 *
 * The service also supports “classifier” entity types that are loaded as lists and then
 * exposed as individual cached items.
 */
export const CLASSIFIER_ENTITY_TYPES_DATA = new InjectionToken<any[]>('', {
    providedIn: 'root',
    factory: () => [],
});

/**
 * Injection token that defines which entity types should be treated as “classifier” types.
 *
 * When an entity type is present in this list, `getEntity$()` routes to `getClassifierEntity$()`
 * which loads the full list and then resolves an item from storage.
 *
 * Default value is an empty array (no classifier types).
 *
 * You can override it in providers:
 *
 * ```ts
 * providers: [
 *   { provide: CLASSIFIER_ENTITY_TYPES_DATA, useValue: ['classifier.some_type', 'classifier.other_type'] },
 * ]
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class BazisEntityService {
    protected srv = inject(BazisSrvService);

    protected storage = inject(BazisStorageService);

    protected modalService = inject(BazisModalService);

    protected toastService = inject(BazisToastService);

    protected configurationService = inject(BazisConfigurationService);

    protected classifierTypes = inject(CLASSIFIER_ENTITY_TYPES_DATA);

    /**
     * Create the service.
     *
     * @param srv - Low-level API transport service used for HTTP calls and request building.
     * @param storage - Client storage/cache for entities, lists, and schemas.
     * @param modalService - Modal service used for transit flows that require user input.
     * @param toastService - Toast service used to show feedback for some operations (example: copy).
     * @param configurationService - Configuration service used to resolve schema by entity type.
     * @param classifierTypes - List of entity types treated as classifiers (injected via `CLASSIFIER_ENTITY_TYPES_DATA`).
     */
    /**
     * Cache of in-flight requests (entities, lists, classifier list loads).
     * Key format depends on method (example: `${entityType}-${id}`).
     */
    protected _getRequests = {};

    /**
     * Cache of in-flight schema requests.
     * Key format: `${schemaType}-${entityType}-${id}`.
     */
    protected _getSchemaRequests = {};

    // doNotInitLoad - только если в логике 100% будет одновременно вызываться forceLoad
    /**
     * Get an entity as an Observable, using cache and optional forced reload.
     *
     * Behavior:
     * - If `entityType` is a classifier type, delegates to `getClassifierEntity$()`.
     * - Uses `BazisStorageService` to check and read cached items.
     * - Uses `_getRequests` to reuse an in-flight request when possible.
     * - When request returns `included`, stores included entities into storage.
     * - When `fields` are provided for an entity type, merges data instead of replacing it.
     * - When the request finalizes and entity is still missing, removes storage item and clears request cache.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @param options - Load options.
     * @param options.forceLoad - If true, forces API reload even when cached. Default: false.
     * @param options.doNotInitLoad - If true, does not start API request (only returns storage observable). Default: false.
     * @param options.include - Relationships to include from backend. Default: [].
     * @param options.skipErrorToast - If true, suppress API error toast. Default: false.
     * @param options.fields - Optional fields map for sparse fieldsets. Default: null.
     * @param options.isBackgroundRequest - If true, marks request as background (backend/client behavior). Default: false.
     * @returns Observable of entity `EntData` (may emit `null`/`undefined` depending on storage state).
     */
    getEntity$(
        entityType: string,
        id: string = '',
        {
            forceLoad = false,
            doNotInitLoad = false,
            include = [],
            skipErrorToast = false,
            fields = null,
            isBackgroundRequest = false,
        }: {
            forceLoad?: boolean;
            doNotInitLoad?: boolean;
            include?: string[];
            skipErrorToast?: boolean;
            fields?: SrvRequestFields;
            isBackgroundRequest?: boolean;
        } = {},
    ): Observable<EntData> {
        if (this.classifierTypes.indexOf(entityType) > -1)
            return this.getClassifierEntity$(entityType, id);

        // проверяем существование сущности
        const isExistedItem = this.storage.isExistedItem(entityType, id);
        // если ее нет или надо обязательно перезапросить или сейчас в процессе запроса
        const path = `${entityType}-${id}`;
        if (this._getRequests[path] && !forceLoad) {
            return this._getRequests[path];
        }

        if (
            (!isExistedItem || forceLoad || !this.storage.getItem(entityType, id)) &&
            !doNotInitLoad
        ) {
            // создаем в storage item$, в который после получения сущности ее отправим
            this.storage.getItem$(entityType, id);

            // если это обычная сущность, то просто ее получаем
            // сохраняем запрос, чтобы не "потерять сущность при отписке async", если запрос еще не был к моменту отписки завершен
            this._getRequests[path] = this.srv
                .fetchEntity$(entityType, id, include, fields, skipErrorToast)
                .pipe(
                    switchMap((r) => {
                        // сохраняем в storage
                        if (r.included && r.included.length > 0) {
                            r.included.forEach((includedItem) => {
                                this.storage.getItem$(includedItem.type, includedItem.id);

                                if (fields && fields[includedItem.type]) {
                                    this.storage.mergeItems(
                                        includedItem.type,
                                        includedItem.id,
                                        includedItem,
                                    );
                                } else {
                                    this.storage.setItem(
                                        includedItem.type,
                                        includedItem,
                                        includedItem.id,
                                    );
                                }
                            });
                            delete r.included;
                        }
                        if (entityType === 'statusy.transit') {
                            r.$snapshot = {
                                ...r.$snapshot,
                                ...this.srv.makeupTransitPayload(
                                    r.$snapshot.payload_schema?.properties,
                                    r.$snapshot.payload_schema?.definitions,
                                ),
                            };
                        }
                        if (fields && fields[entityType]) {
                            this.storage.mergeItems(entityType, id, r);
                        } else {
                            this.storage.setItem(entityType, r, id);
                        }
                        delete this._getRequests[path];
                        return this.storage.getItem$(entityType, id);
                    }),
                    finalize(() => {
                        // если в момент finalize нет данных по объекту, то удаляем и информацию о запросе, и данные в storage
                        if (!this.storage.getItem(entityType, id)) {
                            this.storage.removeItem('entity', entityType, id);
                            delete this._getRequests[path];
                        }
                    }),
                    shareReplay(SHARE_REPLAY_SETTINGS),
                );

            // + возвращаем этот запрос, если
            return this._getRequests[path];
        }

        // если уже есть сущность, значит, просто ее возвращаем
        return this.storage.getItem$(entityType, id);
    }

    /**
     * Resolve a classifier entity item by first loading full classifier list.
     *
     * The method calls `getEntityList$()` with `saveSeparateItems: true`,
     * then resolves a single item from storage by id.
     *
     * @param entityType - Classifier entity type string.
     * @param entityId - Entity id string.
     * @returns Observable of entity `EntData`.
     */
    protected getClassifierEntity$(entityType, entityId): Observable<EntData> {
        const isExistedItem = this.storage.isExistedItem(entityType, entityId);
        if (isExistedItem) return this.storage.getItem$(entityType, entityId);

        const path = `classifier-${entityType}`;

        this._getRequests[path] ||= this.getEntityList$(entityType, {
            saveSeparateItems: true,
            limit: 1000,
            meta: [],
            params: {
                sort: null,
            },
        }).pipe(shareReplay());

        return this._getRequests[path].pipe(
            switchMap(() => this.storage.getItem$(entityType, entityId)),
            tap(() => {
                delete this._getRequests[path];
            }),
            finalize(() => {
                delete this._getRequests[path];
            }),
            shareReplay(),
        );
    }

    /**
     * Resolve organization info entity for a given organization owner id.
     *
     * Loads `organization.organization_info` list filtered by `org_owner` and saves the first result as an item.
     * Uses request caching in `_getRequests` similarly to `getEntity$()`.
     *
     * @param orgOwnerId - Organization owner id string.
     * @param forceLoad - If true, forces reload even if request exists. Default: false.
     * @returns Observable of organization info `EntData` or `null`.
     */
    getOrganizationEntity$(orgOwnerId: string, forceLoad = false): Observable<EntData> {
        // проверяем существование сущности
        const entityType = 'organization.organization_info';
        const id = orgOwnerId;
        const isExistedItem = this.storage.isExistedItem(entityType, orgOwnerId);
        // если ее нет или надо обязательно перезапросить или сейчас в процессе запроса
        const path = `${entityType}-${id}`;
        if (this._getRequests[path] && !forceLoad) {
            return this._getRequests[path];
        }

        if (!isExistedItem || forceLoad) {
            // создаем в storage item$, в который после получения сущности ее отправим
            this.storage.getItem$(entityType, id);

            // если это обычная сущность, то просто ее получаем
            // сохраняем запрос, чтобы не "потерять сущность при отписке async", если запрос еще не был к моменту отписки завершен
            this._getRequests[path] = this.getEntityList$('organization.organization_info', {
                params: {
                    filter: buildFilterStr({ org_owner: id }),
                },
                limit: 1,
            }).pipe(
                map((v) => (v.list && v.list.length > 0 ? v.list[0] : null)),
                switchMap((r) => {
                    this.storage.setItem(entityType, r, id);
                    delete this._getRequests[path];
                    return this.storage.getItem$(entityType, id);
                }),
                tap((v) => {
                    delete this._getRequests[path];
                }),
                finalize(() => {
                    if (!this.storage.getItem(entityType, id)) {
                        this.storage.removeItem('entity', entityType, id);
                        delete this._getRequests[path];
                    }
                }),
                shareReplay(SHARE_REPLAY_SETTINGS),
            );
            return this._getRequests[path];
        }

        // если уже есть сущность, значит, просто ее возвращаем
        return this.storage.getItem$(entityType, id);
    }

    // sync method to get entity from storage
    /**
     * Get entity synchronously from storage cache.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @returns Entity `EntData` from storage, or `null`/`undefined` when missing.
     */
    getEntity(entityType: string, id: string = ''): EntData {
        return this.storage.getItem(entityType, id);
    }

    /**
     * Save entity into storage (replace item).
     *
     * @param entity - Entity object to store. Must include `type` and `id` and `$snapshot`.
     * @param desiredId - Optional id override used as storage key.
     * @returns void
     */
    setEntity(entity, desiredId = null) {
        this.storage.getItem$(entity.type, desiredId || entity.id);
        this.storage.setItem(entity.type, entity, desiredId || entity.id);
    }

    /**
     * Merge entity data into storage item.
     *
     * @param entity - Entity object to merge.
     * @param desiredId - Optional id override used as storage key.
     * @returns void
     */
    mergeEntity(entity, desiredId = null) {
        this.storage.getItem$(entity.type, desiredId || entity.id);
        this.storage.mergeItems(entity.type, desiredId || entity.id, entity);
    }

    /**
     * Delete entity in backend and remove it from storage.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @returns Observable that completes after the first value.
     */
    deleteEntity$(entityType: string, id: string = '') {
        return this.srv.deleteEntity$(entityType, id).pipe(
            tap(() => {
                this.storage.removeItem('entity', entityType, id);
            }),
            take(1),
        );
    }

    /**
     * Update entity priority in backend and patch cached entity in storage.
     *
     * After successful API call, the method patches entity snapshot field specified by
     * `prioritySettings.property` with `prioritySettings.value`.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @param prioritySettings - Priority config that defines target property, id and value.
     * @param isBackgroundRequest - If true, performs request as background. Default: false.
     * @returns Observable that completes after the first value.
     */
    setPriority$(
        entityType: string,
        id: string,
        prioritySettings: EntPriority,
        isBackgroundRequest = false,
    ) {
        return this.srv
            .setPriority$(
                entityType,
                id,
                prioritySettings.value,
                prioritySettings.id,
                isBackgroundRequest,
            )
            .pipe(
                tap(() => {
                    this.storage.patchItem(entityType, id, {
                        [prioritySettings.property]: prioritySettings.value,
                    });
                }),
                take(1),
            );
    }

    // sync method to get entity from storage
    /**
     * Get schema synchronously from storage cache.
     *
     * @param schemaType - Schema type (example: `schema_view`, `schema_edit`, `schema_create`).
     * @param entityType - Entity type string.
     * @param id - Optional entity id for id-specific schemas. Default: empty string.
     * @returns Cached schema (`EntSchema`) or `null`/`undefined` when missing.
     */
    getSchema(schemaType: SchemaType, entityType: string, id: string = ''): EntSchema {
        return this.storage.getSchema(schemaType, entityType, id);
    }

    /**
     * Get schema as an Observable, using cache and optional forced reload.
     *
     * Stores schema in storage and caches in-flight request in `_getSchemaRequests`.
     * Also handles `included` schemas returned by backend by storing them separately
     * (as normal schemas or as parent-derived schemas).
     *
     * @param schemaType - Schema type (example: `schema_view`, `schema_edit`, `schema_create`).
     * @param entityType - Entity type string.
     * @param id - Optional entity id for id-specific schemas. Default: empty string.
     * @param forceLoad - If true, forces reload even if cached. Default: false.
     * @param include - Optional include list passed to backend. Default: [].
     * @returns Observable of schema (`EntSchema`).
     */
    getSchema$(
        schemaType: SchemaType,
        entityType: string,
        id: string = '',
        forceLoad = false,
        include: string[] = [],
    ): Observable<EntSchema> {
        // проверяем существование сущности
        const isExistedSchema = this.storage.isExistedSchema(schemaType, entityType, id);
        const path = `${schemaType}-${entityType}-${id}`;
        if (this._getSchemaRequests[path] && !forceLoad) {
            return this._getSchemaRequests[path];
        }
        // если ее нет или надо обязательно перезапросить
        if (!isExistedSchema || forceLoad) {
            // создаем в storage item$, в который после получения сущности ее отправим
            this.storage.getSchema$(schemaType, entityType, id);

            // получаем
            this._getSchemaRequests[path] = this.srv
                .fetchSchema$(schemaType, entityType, id, include)
                .pipe(
                    switchMap((r: EntSchema) => {
                        // сохраняем в storage
                        if (r.included) {
                            r.included.forEach((schema) => {
                                if (schema.schemaType === 'schema_create' || schema.id) {
                                    this.storage.getSchema$(
                                        schema.schemaType,
                                        schema.entityType,
                                        schema.id,
                                    );
                                    this.storage.setSchema(
                                        schema.schemaType,
                                        schema.entityType,
                                        schema,
                                        schema.id,
                                    );
                                } else {
                                    this.storage.getSchemaFromParent$(
                                        schema.schemaType,
                                        schema.entityType,
                                        entityType,
                                        id,
                                    );
                                    this.storage.setSchemaFromParent(
                                        schema.schemaType,
                                        schema.entityType,
                                        entityType,
                                        id,
                                        schema,
                                    );
                                }
                            });
                            delete r.included;
                        }
                        this.storage.setSchema(schemaType, entityType, r, id);
                        return this.storage.getSchema$(schemaType, entityType, id);
                    }),
                    tap((v) => {
                        delete this._getSchemaRequests[path];
                    }),
                    finalize(() => {
                        // если в момент finalize нет данных по объекту, то удаляем и информацию о запросе, и данные в storage
                        if (!this.storage.getSchema(schemaType, entityType, id)) {
                            this.storage.removeItem('schema', entityType, id, schemaType);
                            delete this._getSchemaRequests[path];
                        }
                    }),
                    shareReplay(SHARE_REPLAY_SETTINGS),
                );

            return this._getSchemaRequests[path];
        }

        // если уже есть сущность, значит, просто ее возвращаем
        return this.storage.getSchema$(schemaType, entityType, id);
    }

    /**
     * Get schema by parent context.
     *
     * Returns parent-derived schema from storage if available, otherwise falls back to `getSchema$()`.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id used when fallback to `getSchema$()` is needed.
     * @param parentType - Parent entity type string.
     * @param parentId - Parent entity id string.
     * @returns Observable of schema (`EntSchema`).
     */
    getParentSchema$(
        schemaType: SchemaType,
        entityType: string,
        id: string,
        parentType: string,
        parentId: string,
    ): Observable<EntSchema> {
        // проверяем существование схемы
        const isExistedSchema = this.storage.isExistedSchemaFromParent(
            schemaType,
            entityType,
            parentType,
            parentId,
        );

        if (isExistedSchema) {
            return this.storage.getSchemaFromParent$(schemaType, entityType, parentType, parentId);
        }

        return this.getSchema$(schemaType, entityType, id);
    }

    /**
     * Synchronous version of `getParentSchema$()`.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id used when fallback to `getSchema()` is needed.
     * @param parentType - Parent entity type string.
     * @param parentId - Parent entity id string.
     * @returns Cached schema (`EntSchema`) or `null`/`undefined` when missing.
     */
    getParentSchema(
        schemaType: SchemaType,
        entityType: string,
        id: string,
        parentType: string,
        parentId: string,
    ): EntSchema {
        // проверяем существование схемы
        const isExistedSchema = this.storage.isExistedSchemaFromParent(
            schemaType,
            entityType,
            parentType,
            parentId,
        );

        if (isExistedSchema) {
            return this.storage.getSchemaFromParent(schemaType, entityType, parentType, parentId);
        }

        return this.getSchema(schemaType, entityType, id);
    }

    /**
     * Load and cache full entities list for an entity type in storage.
     *
     * Uses `_getRequests` to reuse in-flight requests.
     * When `saveAsIndividualEntities` is true, also saves each list item as an individual entity item.
     *
     * @param entityType - Entity type string.
     * @param forceLoad - If true, forces reload even if cached. Default: false.
     * @param saveAsIndividualEntities - If true, save list items as separate entities. Default: false.
     * @param fields - Optional fields map for sparse fieldsets. Default: null.
     * @param isBackgroundRequest - If true, performs request as background. Default: false.
     * @returns Observable of entities list (`EntList`).
     */
    getAllEntitiesList$(
        entityType: string,
        forceLoad = false,
        saveAsIndividualEntities = false,
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ): Observable<EntList> {
        // проверяем существование списка
        const isExistedList = this.storage.isExistedList(entityType);

        if (this._getRequests[entityType] && !forceLoad) {
            return this._getRequests[entityType];
        }

        // если списка нет или его надо в обязательном порядке перегрузить
        if (!isExistedList || forceLoad) {
            this.storage.getList$(entityType);
            this._getRequests[entityType] = this.srv
                .fetchAllEntities$(entityType, '', null, fields, isBackgroundRequest)
                .pipe(
                    switchMap((r) => {
                        // сохраняем в storage
                        this.storage.setList(entityType, r);
                        if (saveAsIndividualEntities) {
                            r.list.forEach((v) => {
                                this.storage.getItem$(v.type, v.id);
                                if (fields && fields[entityType]) {
                                    this.storage.mergeItems(v.type, v.id, v);
                                } else {
                                    this.storage.setItem(v.type, v, v.id);
                                }
                            });
                        }
                        return this.storage.getList$(entityType);
                    }),
                    tap(() => {
                        delete this._getRequests[entityType];
                    }),
                    catchError((e) => {
                        delete this._getRequests[entityType];
                        return e;
                    }),
                    shareReplay(SHARE_REPLAY_SETTINGS),
                );

            return this._getRequests[entityType];
        }

        // если уже есть список, значит, просто его возвращаем
        return this.storage.getList$(entityType);
    }

    // не использует storage - для построения динамических списков с подскроллом, зависящих от внешних параметров
    /**
     * Load an entity list portion from backend without using list cache in storage.
     *
     * This method is intended for dynamic lists that depend on external parameters
     * (example: infinite scroll).
     *
     * Optional behavior: when `saveSeparateItems` is true, saves each list item as an individual
     * entity in storage (merge or set depending on `fields`).
     *
     * @param entityType - Entity type string.
     * @param options - List request options.
     * @param options.suffix - Optional endpoint suffix. Default: empty string.
     * @param options.search - Search string. Default: empty string.
     * @param options.params - Query params object. Default: null.
     * @param options.offset - Pagination offset. Default: 0.
     * @param options.limit - Page size. Default: 20.
     * @param options.meta - Meta fields list. Default: [].
     * @param options.saveSeparateItems - If true, saves items into storage. Default: false.
     * @param options.fields - Optional sparse fieldsets map. Default: null.
     * @param options.isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of `EntList`.
     */
    getEntityList$(
        entityType: string,
        {
            suffix = '',
            search = '',
            params = null,
            offset = 0,
            limit = 20,
            meta = [],
            saveSeparateItems = false,
            fields = null,
            isBackgroundRequest = false,
        }: {
            suffix?: string;
            search?: string;
            params?: any;
            offset?: number;
            limit?: number;
            meta?: string[];
            saveSeparateItems?: boolean;
            fields?: SrvRequestFields;
            isBackgroundRequest?: boolean;
        } = {},
    ): Observable<EntList> {
        params = { ...params };
        if (params.sort === null || entityType.indexOf('classifier') === 0) {
            delete params.sort;
        } else if (!params.sort) {
            params.sort = '-dt_created';
        }

        return this.srv
            .fetchPortion$(
                entityType,
                suffix,
                offset,
                limit,
                search,
                params,
                meta,
                fields,
                isBackgroundRequest,
            )
            .pipe(
                tap((list) => {
                    if (!saveSeparateItems) return;
                    if (fields && fields[entityType]) {
                        list.list.forEach((entity) => {
                            this.mergeEntity(entity);
                        });
                    } else {
                        list.list.forEach((entity) => {
                            this.setEntity(entity);
                        });
                    }
                }),
            );
    }

    /**
     * Load all entities for a type by requesting pages until all items are fetched.
     *
     * Uses `apiLimit = 1000` and requests remaining pages in parallel using `forkJoin`.
     * Optional behavior: when `saveSeparateItems` is true, saves each list item as an individual
     * entity in storage (merge or set depending on `fields`).
     *
     * @param entityType - Entity type string.
     * @param options - List request options (similar to `getEntityList$`, without pagination).
     * @returns Observable of merged `EntList`.
     */
    getAllEntityList$(
        entityType: string,
        {
            suffix = '',
            search = '',
            params = null,
            meta = [],
            saveSeparateItems = false,
            fields = null,
            isBackgroundRequest = false,
        }: {
            suffix?: string;
            search?: string;
            params?: any;
            meta?: string[];
            saveSeparateItems?: boolean;
            fields?: SrvRequestFields;
            isBackgroundRequest?: boolean;
        } = {},
    ): Observable<EntList> {
        const apiLimit = 1000;
        params = { ...params };
        if (params.sort === null || entityType.indexOf('classifier') === 0) {
            delete params.sort;
        } else if (!params.sort) {
            params.sort = '-dt_created';
        }

        return this.srv
            .fetchPortion$(
                entityType,
                suffix,
                0,
                apiLimit,
                search,
                params,
                meta,
                fields,
                isBackgroundRequest,
            )
            .pipe(
                switchMap((firstPage: EntList) => {
                    const totalPages = Math.ceil(firstPage.$meta.pagination.count / apiLimit);
                    const requests = [];
                    for (let i = 1; i < totalPages; i++) {
                        requests.push(
                            this.srv.fetchPortion$(
                                entityType,
                                suffix,
                                i * apiLimit,
                                apiLimit,
                                search,
                                params,
                                meta,
                            ),
                        );
                    }

                    return forkJoin([of(firstPage), ...requests]).pipe(
                        map((pages: EntList[]) =>
                            pages.reduce(
                                (acc, curr) => {
                                    acc.list = [...acc.list, ...curr.list];
                                    return acc;
                                },
                                { list: [] },
                            ),
                        ),
                        tap((mergedList) => {
                            if (!saveSeparateItems) return;

                            if (fields && fields[entityType]) {
                                mergedList.list.forEach((entity) => {
                                    this.mergeEntity(entity);
                                });
                            } else {
                                mergedList.list.forEach((entity) => {
                                    this.setEntity(entity);
                                });
                            }
                        }),
                    );
                }),
            );
    }

    // полученные в листе данные сохраняем как отдельные сущности и их возвращаем
    // (!!! следить самостоятельно за мета, листы не всю возвращают)
    /**
     * Load a list portion and then return the same items as individual entity Observables.
     *
     * Flow:
     * - Calls `getEntityList$()`
     * - Saves list items into storage (merge or set depending on `fields`)
     * - Returns `combineLatest` of `getEntity$()` for each list item id with `doNotInitLoad: true`
     *
     * @param entityType - Entity type string.
     * @param options - List request options.
     * @returns Observable of entities array (`EntData[]`).
     */
    getEntityListAsSeparateItems$(
        entityType: string,
        {
            suffix = '',
            search = '',
            params = null,
            offset = 0,
            limit = 20,
            meta = [],
            fields = null,
            isBackgroundRequest = false,
        }: {
            suffix?: string;
            search?: string;
            params?: any;
            offset?: number;
            limit?: number;
            meta?: string[];
            fields?: SrvRequestFields;
            isBackgroundRequest?: boolean;
        } = {},
    ): Observable<EntData[]> {
        return this.getEntityList$(entityType, {
            suffix,
            search,
            params,
            offset,
            limit,
            meta,
            fields,
            isBackgroundRequest,
        }).pipe(
            tap((list) => {
                if (fields && fields[entityType]) {
                    list.list.forEach((entity) => {
                        this.mergeEntity(entity);
                    });
                } else {
                    list.list.forEach((entity) => {
                        this.setEntity(entity);
                    });
                }
            }),
            switchMap((list) =>
                combineLatest(
                    list.list.map((v) =>
                        this.getEntity$(entityType, v.id, { doNotInitLoad: true }),
                    ),
                ),
            ),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    /**
     * Request counts for multiple list settings.
     *
     * Delegates to `BazisSrvService.countRequest$()` and returns a simple number array.
     *
     * @param settings - Array of count settings: `{ entityType, filters, filterParams?, params? }`.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of counts array.
     */
    getListsCount$(
        settings: { entityType: string; filters: any; filterParams?: any; params?: any }[],
        isBackgroundRequest = false,
    ): Observable<number[]> {
        return this.srv
            .countRequest$(settings, isBackgroundRequest)
            .pipe(map((responses) => responses.map((response) => response.count || 0)));
    }

    /**
     * Convert simple data object to `EntData` shape.
     *
     * If `data` already has `$snapshot`, returns it as `EntData`.
     * Otherwise, wraps object into `{ id, type: 'any', $snapshot: {...data} }`.
     *
     * @param data - Simple object or entity.
     * @returns Entity-like object (`EntData`).
     */
    toEntityItem(data: SimpleData | EntData): EntData {
        return data.$snapshot
            ? (data as EntData)
            : {
                  id: data.id,
                  $snapshot: {
                      ...data,
                  },
                  type: 'any',
              };
    }

    /**
     * Convert an array of simple objects or entities to an `EntList` object.
     *
     * @param array - Array of `SimpleData` or `EntData`.
     * @returns List object with `list` of `EntData` items.
     */
    toEntitiesList(array: SimpleData[] | EntData[]): EntList {
        return {
            list: array.map((arrayItem) => this.toEntityItem(arrayItem)),
        };
    }

    /**
     * Build transit request settings, optionally asking user for payload via modal.
     *
     * For each transit item with `payload` or `hint`, opens a modal (`BazisTransitModalComponent` by default)
     * and waits for user input. When user cancels, returns `{ status: 'cancel', transitSettings: null }`.
     *
     * @param settings - Transit items list.
     * @param transitSettings - Accumulator for built settings. Default: [].
     * @returns Observable of `{ status, transitSettings }`.
     */
    protected _buildTransitData$(
        settings: {
            entityType?: string;
            entityId?: string;
            url?: string;
            transit: string;
            payload?: any;
            hint?: string;
            modalComponent?;
        }[],
        transitSettings = [],
    ): Observable<{ status: string; transitSettings: any }> {
        for (let i = transitSettings.length; i < settings.length; i++) {
            const transitItem = settings[i];

            if (transitItem.payload || transitItem.hint) {
                if (!transitItem.modalComponent) {
                    transitItem.modalComponent = BazisTransitModalComponent;
                }
                const modal = this.modalService.create({
                    component: transitItem.modalComponent,
                    componentProperties: {
                        payload: transitItem.payload,
                        hint: transitItem.hint,
                        transit: transitItem.transit,
                    },
                    hasCloseIcon: false,
                    styleAlert: true,
                });

                return from(modal.onDidDismiss()).pipe(
                    switchMap((payloadBody) => {
                        if (payloadBody === null) {
                            return of({
                                status: 'cancel',
                                transitSettings: null,
                            });
                        }
                        transitSettings.push({
                            entityType: transitItem.entityType,
                            entityId: transitItem.entityId,
                            url: transitItem.url,
                            transitParams: {
                                transit: transitItem.transit,
                                payload: payloadBody,
                            },
                        });
                        return this._buildTransitData$(settings, transitSettings);
                    }),
                    take(1),
                );
            }

            transitSettings.push({
                entityType: transitItem.entityType,
                entityId: transitItem.entityId,
                url: transitItem.url,
                transitParams: {
                    transit: transitItem.transit,
                    payload: null,
                },
            });
        }

        if (transitSettings.length === settings.length) {
            return of({
                status: 'ready',
                transitSettings,
            });
        }
    }

    /**
     * Perform one or multiple transit operations.
     *
     * Builds transit payload list using `_buildTransitData$()` and then calls `srv.transitEntity$()`.
     * When user cancels payload modal, returns `onCancelResponse` as Observable value.
     *
     * @param settings - Transit items list (each item can be defined by entityType/entityId or by explicit url).
     * @param onCancelResponse - Value to emit when user cancels transit payload input. Default: `null`.
     * @param isBackgroundRequest - If true, performs transit as background request. Default: false.
     * @returns Observable of transit response (shape depends on backend and number of items).
     */
    transitEntity$(
        settings: {
            entityType?: string;
            entityId?: string;
            url?: string;
            transit: string;
            payload?: any;
            hint?: string;
            modalComponent?: any;
        }[],
        onCancelResponse = null,
        isBackgroundRequest = false,
    ) {
        return this._buildTransitData$(settings).pipe(
            filter((response) => response.status === 'cancel' || response.status === 'ready'),
            switchMap((response) =>
                response.status === 'cancel'
                    ? of(onCancelResponse)
                    : this.srv.transitEntity$(response.transitSettings, isBackgroundRequest),
            ),
        );
    }

    /**
     * Copy an entity one or multiple times using bulk group request.
     *
     * Builds new entity bodies from `fields` list and applies optional `valueModifiers` per copy.
     * On success, shows a success toast.
     *
     * @param entity - Source entity (`EntData`-like object with `type` and `$snapshot`).
     * @param entitySchema - Entity schema object used to decide whether a field is attribute or relationship.
     * @param fields - List of field names to copy from source entity.
     * @param count - Number of copies to create. Default: 1.
     * @param valueModifiers - Array of partial objects to override values per copy. Default: [].
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of created entities array.
     */
    copyEntity$(
        entity,
        entitySchema,
        fields,
        count = 1,
        valueModifiers = [],
        isBackgroundRequest = false,
    ): Observable<EntData[]> {
        const newEntity = {
            data: {
                type: entity.type,
                attributes: {},
                relationships: {},
            },
        };
        fields.forEach((field) => {
            if (entitySchema.attributes[field]) {
                newEntity.data.attributes[field] = entity.$snapshot[field];
            } else {
                newEntity.data.relationships[field] = {
                    data: entity.$snapshot[field],
                };
            }
        });
        const items = [];
        count = Math.max(count, valueModifiers.length);

        for (let i = 0; i < count; i++) {
            items.push(JSON.parse(JSON.stringify(newEntity)));
        }

        valueModifiers.forEach((item, i) => {
            const modifiedFields = Object.keys(item);
            modifiedFields.forEach((field) => {
                if (entitySchema.attributes[field]) {
                    items[i].data.attributes[field] = item[field];
                } else {
                    items[i].data.relationships[field] = {
                        data: item[field],
                    };
                }
            });
        });

        return this.srv
            .groupRequest$(
                items.map((item) => {
                    return {
                        entityId: null,
                        entityType: item.data.type,
                        requestType: 'item',
                        body: item,
                        method: 'POST',
                        params: null,
                        meta: [],
                    };
                }),
                isBackgroundRequest,
            )
            .pipe(
                tap((v) => {
                    this.toastService.create({
                        type: 'success',
                        titleKey: 'copyEntity.success.title.' + entity.type,
                        messageKey: 'copyEntity.success.description.' + entity.type,
                    });
                }),
            );
    }

    /**
     * Copy an entity using backend `.../copy` form endpoint.
     *
     * On success, shows a success toast.
     *
     * @param entity - Entity description `{ type, id }`.
     * @returns Observable of backend response.
     */
    apiCopyEntity$(entity: EntDescription) {
        return this.srv
            .sendFormRequest$(`${entity.type.split('.').join('/')}/${entity.id}/copy`)
            .pipe(
                tap((v) => {
                    this.toastService.create({
                        type: 'success',
                        titleKey: 'copyEntity.success.title.' + entity.type,
                        messageKey: 'copyEntity.success.description.' + entity.type,
                    });
                }),
            );
    }

    // TODO: included entities need to be tested!!!
    /**
     * Create an entity using JSON:API body generator and optional included entities.
     *
     * @param entityType - Entity type string.
     * @param attributes - Attributes object to send. Default: {}.
     * @param relationships - Relationships map where value is id or ids list. Default: {}.
     * @param include - Include list for backend response. Default: [].
     * @param desiredId - Optional client-provided id for new entity. Default: null.
     * @param includedEntities - Included entities list to include in request body. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of created entity `EntData`.
     */
    createEntity$(
        entityType,
        attributes = {},
        relationships: { [index: string]: string | string[] } = {},
        include = [],
        desiredId = null,
        includedEntities: IncludedEntData[] = [],
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ) {
        const relationshipsData = this.buildEntityRelationships(entityType, relationships);
        const data = this.srv.generateEntityBody(
            entityType,
            desiredId,
            attributes,
            relationshipsData,
        );
        const included = this.buildEntityIncluded(includedEntities);
        const body = !included.length ? { ...data } : { ...data, included };
        return this.srv.createEntity$(entityType, body, include, fields, isBackgroundRequest);
    }

    // TODO: included entities need to be tested!!!
    /**
     * Update an entity using JSON:API body generator and optional included entities.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @param attributes - Attributes object to send. Default: {}.
     * @param relationships - Relationships data object (already in JSON:API relationship shape). Default: {}.
     * @param include - Include list for backend response. Default: [].
     * @param includedEntities - Included entities list to include in request body. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of updated entity `EntData`.
     */
    updateEntity$(
        entityType,
        id,
        attributes = {},
        relationships: relationshipsEntData = {},
        include = [],
        includedEntities: IncludedEntData[] = [],
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ) {
        const relationshipsData = this.buildEntityRelationships(entityType, relationships);
        const data = this.srv.generateEntityBody(entityType, id, attributes, relationshipsData);
        const included = this.buildEntityIncluded(includedEntities);
        const body = !included.length ? { ...data } : { ...data, included };
        return this.srv.saveEntity$(entityType, id, body, include, fields, isBackgroundRequest);
    }

    /**
     * Build `included` array for JSON:API request body from internal `IncludedEntData` format.
     *
     * @param includedEntities - List of included entities settings.
     * @returns Array of included entities for request body.
     */
    buildEntityIncluded(includedEntities: IncludedEntData[] = []): any[] {
        return includedEntities.map((entity) => {
            return {
                id: entity.id,
                type: entity.entityType,
                action: entity.action,
                attributes: entity.attributes,
                relationships: this.buildEntityRelationships(
                    entity.entityType,
                    entity.relationships,
                ),
            };
        });
    }

    /**
     * Build JSON:API relationships object based on configuration schema for the entity type.
     *
     * For schema relationship keys:
     * - `type === 'object'`: converts id to `{ id, type }` object
     * - `type === 'array'`: converts ids list to array of `{ id, type }`
     *
     * For keys missing in schema, stores relationship as-is under `{ data: relationships[key] }`.
     *
     * @param entityType - Entity type string used to read relationship schema from configuration.
     * @param relationships - Relationships map (ids or ids arrays depending on relationship).
     * @returns JSON:API relationships data object.
     */
    buildEntityRelationships(
        entityType: string,
        relationships: relationshipsEntData,
    ): SrvEntRelationshipsData {
        const relationshipsData = {};
        const schema = this.configurationService.getSchemaByEntityType(entityType);
        Object.keys(relationships).forEach((key) => {
            if (!schema.relationships[key]) {
                relationshipsData[key] = {
                    data: relationships[key],
                };
                return;
            }
            if (schema.relationships[key].type === 'object') {
                relationshipsData[key] = {
                    data: relationships[key]
                        ? {
                              id: relationships[key],
                              type: schema.relationships[key].entityType,
                          }
                        : null,
                };
            } else {
                relationshipsData[key] = {
                    data: (relationships[key] as string[])
                        .filter((v) => !!v)
                        .map((id) => ({
                            id: id,
                            type: schema.relationships[key].entityType,
                        })),
                };
            }
        });

        return relationshipsData;
    }
}
